import { cloneDeep, isEqual } from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { treeActions } from "../../../redux/slices/treeSlice";
import { getUpdatedData } from "./NewRecord";
import { RECORD_TYPE, DATA_TYPE } from "../../../constants/datatypes";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { genKey } from "../../../utils/utilFunctions";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

const NewField = ({ toggle, openModal }) => {
  const { t } = useTranslation();
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
  const currentTab = rightTree.currentTab;
  const [showModal,setShowModal] = useState(false);
  const [content,setContent] = useState('');
  const selectedNode = rightTree.tree[currentTab].selectedNode;
  const templateHeader = rightTree.tree.templateHeader;
  const dispatch = useDispatch();
  const handleNewField = (isInside) => {
    let nestedLevels = selectedNode.key.split("-");
    nestedLevels = nestedLevels.splice(1);
    const translatedData = cloneDeep(rightTree.tree[currentTab].translatedData);

    // Function For Getting New Record Title and Key
    const getNewFieldData = (data, key) => {
      //If it is empty so it will make first node with 'ib-0' key
      if (!data.length) return { key: `${key}-0`, title: `${t('UNNAMED')}1`, insertingPosition: 1 ,fieldNumber:1};

      let latestNewNodeNum = 0;
      let insertingPosition;
      let fieldNumber = 1;
      data.forEach((ele, idx) => {
        // Checking if Unnamed is present already for creating next unnamed record
        const fieldTitle = ele?.MAPNAME;
        if (fieldTitle && fieldTitle.startsWith(`${t("UNNAMED")}`)) {
          const latestUnnamedRecord = Number(fieldTitle.slice(t("UNNAMED").length));
          if (!isNaN(latestUnnamedRecord) && DATA_TYPE[templateHeader?.FILETYPE]?.field?.icon === ele?.ICON_DISPLAY) {
            latestNewNodeNum =
              latestUnnamedRecord > latestNewNodeNum
                ? latestUnnamedRecord
                : latestNewNodeNum;
          }
        }
        if (ele.key === selectedNode.key) {
          insertingPosition = idx + 2;
          if(ele.ICON_DISPLAY.includes("FIELD")) fieldNumber++;
        }
        if(!insertingPosition && ele.ICON_DISPLAY.includes("FIELD")){
          fieldNumber++;
        }
      });

      const lastRecordKey = data[data.length - 1].key.split("-");
      lastRecordKey[lastRecordKey.length - 1] = +lastRecordKey[lastRecordKey.length - 1] + 1;
      const newRecordKey = lastRecordKey.join("-");
      return {
        key: newRecordKey,
        title: `${t('UNNAMED')}${latestNewNodeNum ? +latestNewNodeNum + 1 : 1}`,
        insertingPosition,
        fieldNumber:insertingPosition !== undefined ? fieldNumber : 1
      };
    };

    // Inserting New Node Object in Tree Data (Recursive Function);
    function insertNodeInNestedTree(translatedData, nestedLevels, isInside) {
      const level = isInside ? 0 : 1;
      if (nestedLevels.length === level) {
        const children = translatedData.children || [];
        const key = translatedData.key || 'ib';
        const newFieldDetails = (children)
          ? getNewFieldData(children, key)
          : getNewFieldData(translatedData, "ib");
        const newNode = {
          nodeId: genKey(newFieldDetails.title),
          alternateNodeId: genKey(newFieldDetails.title),
          key: newFieldDetails.key,
          title: `${t("LBL_FIELD")} ${newFieldDetails.title}`,
          ICON_DISPLAY: DATA_TYPE[templateHeader?.FILETYPE]?.field?.icon,
          type: "FIELD",
          children: [],
          MAPNAME: newFieldDetails.title,
          fieldNumber: `${templateHeader?.FILETYPE === "EDI" ? 
                        ( selectedNode?.ICON_DISPLAY?.includes("FIELD_GROUP") && 
                          isInside ? newFieldDetails.fieldNumber :newFieldDetails.fieldNumber - 1) 
                          : newFieldDetails.fieldNumber}`,
          // DESCRIPTION: newFieldDetails.title,
          FIELDTYPE: "ALPHA",
        };

        if (children) {
          if (isInside) {
            translatedData.children.splice(0, 0, newNode);
            let newKey = selectedNode.key.split('-');
            newKey.push('0')
            newKey = newKey.join('-');
            dispatch(treeActions.setRightTreeSelectedNode({ 
              screen: parentScreen,
              type: currentTab, 
              data: { ...newNode, key: newKey } 
            }));
          } else {
            translatedData.children.splice(newFieldDetails.insertingPosition - 1, 0, newNode);
            let newKey = selectedNode.key.split('-');
            newKey[newKey.length - 1] = Number(newKey[newKey.length - 1]) + 1;
            newKey = newKey.join('-');
            dispatch(treeActions.setRightTreeSelectedNode({ 
              screen: parentScreen,
              type: currentTab, 
              data: { ...newNode, key: newKey } 
            }));
          }
        }
        return;
      }

      const isArray = Array.isArray(translatedData);
      if (isArray) {
        const nestedData = translatedData[[nestedLevels[0]]];
        const newNestedLevels = nestedLevels.splice(1);
        insertNodeInNestedTree(nestedData, newNestedLevels, isInside);
      } else {
        // const newNestedLevels = nestedLevels.splice(1);
        insertNodeInNestedTree(translatedData.children, nestedLevels, isInside);
      }
    }

    insertNodeInNestedTree(translatedData, nestedLevels, isInside);
    const updatedData = getUpdatedData(translatedData,templateHeader?.FILETYPE, parentScreen, rightTree.currentTab);

    //prevent dispatch for no reason
    if (!isEqual(translatedData, updatedData)) {
      dispatch(
        treeActions.setNewRightTreeTranslatedData({
          screen: parentScreen,
          type: currentTab,
          data: updatedData,
        })
      );
    }
    toggle(null);
    setShowModal(false);
  };

  useEffect(() => {
    if (selectedNode?.ICON_DISPLAY?.includes('FIELD_GROUP') && openModal){
      const content = (t('BOX_LIKE_TO_PLACE_FLD_INSIDE_CURR_FLDGRP_OR_DIRECTLY_AFTER') || '').split('\\n').map((str, idx) => <div key={idx}>{str ? str : <br />}</div>);
      setContent(content)
      setShowModal(true);
    }
    else if (!RECORD_TYPE[selectedNode.ICON_DISPLAY] && openModal) {
      handleNewField(false);
      return;
    } else if (RECORD_TYPE[selectedNode.ICON_DISPLAY] && openModal) {
      handleNewField(true);
      return;
    }
  }, [openModal]);

  return (<Dialog open={showModal} maxWidth="xs" fullWidth>
    <DialogTitle className="dialog-header">
      <div className="title">{t("BOX_CONFIRM")}</div>
    </DialogTitle>
    <DialogContent className="dialog-content" dividers>
      <div>{content}</div>
    </DialogContent>
    <DialogActions>
      <Button
        size='small'
        variant="outlined"
        color="primary"
        onClick={() => handleNewField(true)}
      >
        {t("BTN_YES")}
      </Button>
      <Button
        size='small'
        variant="outlined"
        color="primary"
        onClick={() => handleNewField(false)}
      >
        {t("BTN_NO")}
      </Button>
      <Button
        size='small'
        variant="outlined"
        color="primary"
        onClick={() => {
          toggle(null);
          setShowModal(false);
        }}
      >
        {t("BTN_CANCEL")}
      </Button>
    </DialogActions>
  </Dialog>);
};

export default NewField;